import React, { useState, useEffect, useCallback } from "react";
import DOMPurify from 'dompurify';
import { fetchFromStrapi } from "../api/strapi";

// Fallback testimonials if Strapi fetch fails
const fallbackTestimonials = [
  {
    quote:
      "ECL has helped to improve our infrastructure, save us time, and enhance the quality of our services. Their team is incredibly proactive – always ready with a solution, no matter the time or day. I highly recommend ECL to other organizations; they are true professionals who genuinely prioritize their clients.",
    author: "Eric Malm",
    role: "CIO, AccessBank Liberia Limited",
  },
  {
    quote:
      "We needed to automate our internal process of handling customer over-scratch complaints. ECL designed and implemented a bot that performed the task accurately and efficiently. We truly appreciate ECL's expertise and commitment to delivering this valuable solution. Thank you!",
    author: "Sanjay Nemade",
    role: "Orange, Liberia",
  },
  {
    quote:
      "At Fidelity Bank, we were losing to repetitive, time-consuming processes that deserved a smarter solution. ECL's RPA changed that. <br/>Across 22 automated processes, the impact has been immediate and measurable:<br/><ul></ul><li>9,000+ hours reclaimed annually</li> <li>VISA ROU Clearing cut from 500 minutes to 25</li> <li>GHS 400,000 in projected annual savings from error reduction</li><li>Airtight compliance, without the manual effort</li><br/>Our people are no longer buried in routine work. They're focused on customers.",
    author: "Prince Agra",
    role: "Fidelity Bank Ghana",
  },
  {
    quote:
      "Working with ECL to deploy our RPA solution was a game-changer for StarLife Assurance. Since launching in March 2023, we have reduced manual underwriting from 85.4% to 7.09%, with smart underwriting increasing to nearly 93%. ECL's expertise was central to making this transformation a reality.",
    author: "Peter Obeng Sekyere",
    role: "Head, Collections and Data Analytics – StarLife Assurance",
  },
];

export default function Testimonials() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState("next");
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTestimonials = async () => {
      try {
        const res = await fetchFromStrapi("/ecl-testimonials");
        const data = res?.data || [];
        
        if (Array.isArray(data) && data.length > 0) {
          // Map Strapi data to component format
          const mappedTestimonials = data.map(item => ({
            quote: item.quote || item.attributes?.quote || "",
            author: item.author || item.attributes?.author || "",
            role: item.role || item.attributes?.role || "",
          }));
          setTestimonials(mappedTestimonials);
          console.log("Testimonials loaded from Strapi:", mappedTestimonials);
        } else {
          setTestimonials(fallbackTestimonials);
        }
      } catch (err) {
        console.error("Error loading testimonials from Strapi:", err);
        setTestimonials(fallbackTestimonials);
      } finally {
        setLoading(false);
      }
    };

    loadTestimonials();
  }, []);
 

  const goTo = useCallback((index, dir = "next") => {
    if (animating) return;
    setDirection(dir);
    setAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 320);
  }, [animating]);

  const next = useCallback(() => {
    goTo((current + 1) % testimonials.length, "next");
  }, [current, goTo]);

  const prev = useCallback(() => {
    goTo((current - 1 + testimonials.length) % testimonials.length, "prev");
  }, [current, goTo]);

  useEffect(() => {
    const id = setInterval(next, 7000);
    return () => clearInterval(id);
  }, [next]);

  const t = testimonials[current];

  return (
    <section className="container" style={{ marginBottom: 120 }}>
      <div style={{ position: "relative" }}>
        {/* Cards row — shows 1 centered + 2 faded side cards on desktop */}
        <div className="d-none d-lg-flex align-items-stretch justify-content-center" style={{ gap: 20, minHeight: 260 }}>
          {/* Prev card (faded) */}
          <div style={{
            flex: "0 0 380px",
            opacity: 0.35,
            filter: "blur(1px)",
            transform: "scale(0.95)",
            transition: "all 0.35s ease",
            pointerEvents: "none",
          }}>
            <div className="testimonial-card" style={{ height: "100%" }}>
              <p style={{ fontSize: "14px", padding: "0 15px", lineHeight: 1.45 }}>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(testimonials[(current - 1 + testimonials.length) % testimonials.length].quote || '') }} />
              </p>
              <div className="author">{testimonials[(current - 1 + testimonials.length) % testimonials.length].author}</div>
              <div className="role">{testimonials[(current - 1 + testimonials.length) % testimonials.length].role}</div>
            </div>
          </div>

          {/* Active card */}
          <div
            style={{
              flex: "0 0 380px",
              opacity: animating ? 0 : 1,
              transform: animating
                ? `translateX(${direction === "next" ? "30px" : "-30px"})`
                : "translateX(0) scale(1)",
              transition: "opacity 0.32s ease, transform 0.32s ease",
            }}
          >
            <div className="testimonial-card" style={{ height: "100%", border: "1px solid rgba(147,216,254,0.45)" }}>
              <p style={{ fontSize: "14px", padding: "0 15px", lineHeight: 1.45 }}>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.quote || '') }} />
              </p>
              <div className="author">{t.author}</div>
              <div className="role">{t.role}</div>
              <div className="text-end mt-2">
                <i className="bi bi-quote" />
              </div>
            </div>
          </div>

          {/* Next card (faded) */}
          <div style={{
            flex: "0 0 380px",
            opacity: 0.35,
            filter: "blur(1px)",
            transform: "scale(0.95)",
            transition: "all 0.35s ease",
            pointerEvents: "none",
          }}>
            <div className="testimonial-card" style={{ height: "100%" }}>
              <p style={{ fontSize: "14px", padding: "0 15px", lineHeight: 1.45 }}>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(testimonials[(current + 1) % testimonials.length].quote || '') }} />
              </p>
              <div className="author">{testimonials[(current + 1) % testimonials.length].author}</div>
              <div className="role">{testimonials[(current + 1) % testimonials.length].role}</div>
            </div>
          </div>
        </div>

        {/* Mobile — single card */}
        <div className="d-lg-none">
          <div
            style={{
              opacity: animating ? 0 : 1,
              transform: animating
                ? `translateX(${direction === "next" ? "20px" : "-20px"})`
                : "translateX(0)",
              transition: "opacity 0.32s ease, transform 0.32s ease",
            }}
          >
            <div className="testimonial-card">
              <p style={{ fontSize: "14px", padding: "0 15px", lineHeight: 1.45 }}>
                <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t.quote || '') }} />
              </p>
              <div className="author">{t.author}</div>
              <div className="role">{t.role}</div>
              <div className="text-end mt-2">
                <i className="bi bi-quote" />
              </div>
            </div>
          </div>
        </div>

        {/* Prev / Next arrows */}
        <button
          onClick={prev}
          aria-label="Previous testimonial"
          style={{
            position: "absolute",
            top: "50%",
            left: -20,
            transform: "translateY(-50%)",
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "1px solid rgba(147,216,254,0.3)",
            background: "rgba(8,20,40,0.85)",
            color: "#98d6f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 2,
            transition: "background 0.2s ease, border-color 0.2s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,158,220,0.25)"; e.currentTarget.style.borderColor = "rgba(0,158,220,0.7)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(8,20,40,0.85)"; e.currentTarget.style.borderColor = "rgba(147,216,254,0.3)"; }}
        >
          <i className="bi bi-chevron-left" style={{ fontSize: 14 }} />
        </button>

        <button
          onClick={next}
          aria-label="Next testimonial"
          style={{
            position: "absolute",
            top: "50%",
            right: -20,
            transform: "translateY(-50%)",
            width: 40,
            height: 40,
            borderRadius: "50%",
            border: "1px solid rgba(147,216,254,0.3)",
            background: "rgba(8,20,40,0.85)",
            color: "#98d6f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 2,
            transition: "background 0.2s ease, border-color 0.2s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(0,158,220,0.25)"; e.currentTarget.style.borderColor = "rgba(0,158,220,0.7)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "rgba(8,20,40,0.85)"; e.currentTarget.style.borderColor = "rgba(147,216,254,0.3)"; }}
        >
          <i className="bi bi-chevron-right" style={{ fontSize: 14 }} />
        </button>
      </div>

      {/* Dot indicators */}
      <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 28 }}>
        {testimonials.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i, i > current ? "next" : "prev")}
            aria-label={`Testimonial ${i + 1}`}
            style={{
              width: i === current ? 24 : 8,
              height: 8,
              borderRadius: 4,
              border: "none",
              padding: 0,
              cursor: "pointer",
              background: i === current ? "#009EDC" : "rgba(147,216,254,0.3)",
              transition: "width 0.3s ease, background 0.3s ease",
            }}
          />
        ))}
      </div>
    </section>
  );
}
